import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export type URLQueryParameter = {
  parameter: string;
  value: string;
};

export const useURLTools = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [queryParams, setQueryParams] = useState<any>();

  const getQueryParams = useCallback((): URLQueryParameter[] => {
    const searchParams = new URLSearchParams(location.search);
    const result: URLQueryParameter[] = [];
    searchParams.forEach((value, parameter) => {
      result.push({ parameter, value });
    });
    return result;
  }, [location.search]);

  useEffect(() => {
    setQueryParams(
      getQueryParams()
        .map((item) => ({ [item.parameter]: item.value }))
        .reduce((acc, curr) => ({ ...acc, ...curr }), {})
    );
  }, [getQueryParams]);

  const updateQueryParams = (params: URLQueryParameter[]): void => {
    const updatedParams = new URLSearchParams(location.search);

    if (params.length === 0) {
      //navigate({ search: "" }, { replace: false });
      return;
    }

    params.forEach(({ parameter, value }) => {
      if (value === "") {
        updatedParams.delete(parameter);
      } else {
        updatedParams.set(parameter, value);
      }
    });

    navigate({ search: updatedParams.toString() }, { replace: false });
  };

  const overwriteQueryParams = (params: URLQueryParameter[]): void => {
    const newParams = new URLSearchParams();
    params.forEach(({ parameter, value }) => {
      if (value !== "") {
        newParams.set(parameter, value);
      }
    });
    navigate({ search: newParams.toString() }, { replace: false });
  };

  const navigateWithQueryParams = (
    path: string,
    params: URLQueryParameter[],
    options?: {
      replace?: boolean;
      openInNewTab?: boolean;
    }
  ) => {
    const searchParams = new URLSearchParams();
    params.forEach(({ parameter, value }) => {
      if (value !== "") {
        searchParams.set(parameter, value);
      }
    });

    const url = `${path}?${searchParams.toString()}`;
    const { replace = false, openInNewTab = false } = options || {};

    if (openInNewTab) {
      window.open(url, "_blank");
    } else {
      navigate(url, { replace });
    }
  };

  return {
    getQueryParams,
    addUpdateRemoveQueryParams: updateQueryParams,
    overwriteQueryParams,
    navigateWithQueryParams,
    queryParams,
  };
};
